import React, { useState, useEffect } from 'react';
import { API_URL } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Building2, UserCircle, MapPin, Loader2, CheckCircle2, Zap } from 'lucide-react';
import PoweredBy from '../components/ui/PoweredBy';

const PublicClientRegisterPage = () => {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', address: '', city: '', postal_code: '' });
  const [companyName, setCompanyName] = useState("Chargement...");
  const [companyLogo, setCompanyLogo] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/settings/public-config`)
        .then(res => res.json())
        .then(data => {setCompanyName(data.company_name);
                        setCompanyLogo(data.logo_base64);})
        .catch(() => setCompanyName("Mon Entreprise"));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/clients/public/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) setDone(true);
      else { const err = await res.json(); toast.error(err.detail || "Erreur lors de l'enregistrement"); }
    } catch (error) { toast.error("Connexion impossible"); }
    finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center p-8 shadow-2xl bg-white border-none text-slate-900">
          <CheckCircle2 className="w-20 h-20 text-emerald-500 mx-auto mb-6 animate-in zoom-in duration-500" />
          <h2 className="text-3xl font-bold mb-3 text-slate-900">Fiche créée !</h2>
          <p className="text-slate-600 mb-8 text-lg">Vos informations ont été enregistrées avec succès dans notre base.</p>
          <Button className="w-full h-12 text-lg" onClick={() => window.location.reload()}>Terminer</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-slate-200 flex flex-col items-center justify-center py-12 px-4 font-sans text-slate-900">
      
      {/* BRANDING HEADER CENTRÉ */}
      <div className="max-w-2xl w-full text-center mb-10 animate-in fade-in slide-in-from-top-4 duration-700">
        <div className="flex justify-center items-center gap-4 mb-4">
          {companyLogo ? (
            <img src={companyLogo} alt="Logo Entreprise" className="h-16 w-auto object-contain drop-shadow-sm" />
          ) : (
            <div className="w-14 h-14 bg-primary rounded-2xl flex items-center justify-center shadow-lg">
              <Zap className="w-7 h-7 text-white fill-white" />
            </div>
          )}
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900">{companyName}</h1>
        </div>
      </div>

      <Card className="max-w-2xl w-full shadow-xl border-slate-200/60 bg-white rounded-2xl overflow-hidden animate-in zoom-in-95 duration-500">
        <div className="bg-slate-50 border-b border-slate-100 p-8 text-center">
            <h2 className="text-2xl font-bold text-slate-900">Espace Client</h2>
            <p className="text-slate-500 mt-2">Créez votre fiche officielle pour faciliter vos démarches et la facturation.</p>
        </div>
        <CardContent className="p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            
            {/* BLOC IDENTITÉ */}
            <div className="space-y-5">
              <div className="flex items-center gap-2 text-primary font-bold text-sm uppercase tracking-wider border-b border-slate-100 pb-2">
                <UserCircle className="w-5 h-5" /> Identité & Contact
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2"><Label className="text-slate-700 font-medium">Nom Complet *</Label><Input required className="bg-slate-50 focus:bg-white text-slate-900" placeholder="Jean Dupont" value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
                <div className="space-y-2"><Label className="text-slate-700 font-medium">Entreprise</Label><Input className="bg-slate-50 focus:bg-white text-slate-900" placeholder="Société SAS" value={form.company} onChange={e => setForm({...form, company: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2"><Label className="text-slate-700 font-medium">Email *</Label><Input type="email" required className="bg-slate-50 focus:bg-white text-slate-900" placeholder="jean@societe.com" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
                <div className="space-y-2"><Label className="text-slate-700 font-medium">Téléphone</Label><Input className="bg-slate-50 focus:bg-white text-slate-900" placeholder="06 12 34 56 78" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
              </div>
            </div>

            {/* BLOC ADRESSE */}
            <div className="space-y-5">
              <div className="flex items-center gap-2 text-primary font-bold text-sm uppercase tracking-wider border-b border-slate-100 pb-2 mt-2">
                <MapPin className="w-5 h-5" /> Adresse de facturation
              </div>
              <div className="space-y-2"><Label className="text-slate-700 font-medium">N° et Libellé de la voie</Label><Input className="bg-slate-50 focus:bg-white text-slate-900" placeholder="12 rue de la Paix" value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2"><Label className="text-slate-700 font-medium">Code Postal</Label><Input className="bg-slate-50 focus:bg-white text-slate-900" placeholder="75000" value={form.postal_code} onChange={e => setForm({...form, postal_code: e.target.value})} /></div>
                <div className="space-y-2"><Label className="text-slate-700 font-medium">Ville</Label><Input className="bg-slate-50 focus:bg-white text-slate-900" placeholder="Paris" value={form.city} onChange={e => setForm({...form, city: e.target.value})} /></div>
              </div>
            </div>

            <div className="pt-4">
                <Button type="submit" className="w-full h-14 text-lg font-bold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5" disabled={loading}>
                {loading ? <Loader2 className="animate-spin mr-2" /> : <Building2 className="mr-2 w-5 h-5" />}
                Enregistrer ma fiche
                </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <footer className="mt-12 text-slate-500 flex flex-col items-center">
        <PoweredBy />
      </footer>
    </div>
  );
};

export default PublicClientRegisterPage;